import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { en } from "./en.ts";
import { fr } from "./fr.ts";
import { DEFAULT_LOCALE, detectLocale, isLocale, type Locale } from "./locales.ts";
import type { Messages } from "./messages.ts";

const DICTIONARIES: Record<Locale, Messages> = { fr, en };

const STORAGE_KEY = "boomap.locale";

interface I18n {
  readonly locale: Locale;
  /** Les textes de la langue courante. Nommé court : il est partout. */
  readonly t: Messages;
  readonly setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * Langue au premier rendu : un choix déjà fait l'emporte sur les préférences du
 * navigateur. Le stockage peut échouer (navigation privée, données de site
 * bloquées) — l'application doit démarrer quand même.
 */
const initialLocale = (): Locale => {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);

    if (isLocale(stored)) {
      return stored;
    }
  } catch {
    // Préférence illisible : on retombe sur le navigateur.
  }

  return detectLocale();
};

export const I18nProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement => {
  const [locale, setState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setState(next);

    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, next);
    } catch {
      // Préférence non mémorisable : sans conséquence pour la session en cours.
    }
  }, []);

  // `lang` sur la racine du document : c'est ce qui pilote la prononciation des
  // lecteurs d'écran, la césure et la sélection des polices du navigateur. La
  // meta description suit, pour que le lien partagé soit dans la bonne langue.
  useEffect(() => {
    document.documentElement.lang = locale;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", DICTIONARIES[locale].app.description);
  }, [locale]);

  const value = useMemo<I18n>(
    () => ({ locale, setLocale, t: DICTIONARIES[locale] }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18n => {
  const value = useContext(I18nContext);

  if (value === null) {
    throw new Error("useI18n doit être utilisé dans un <I18nProvider>.");
  }

  return value;
};

export { DEFAULT_LOCALE, type Locale };
